import { ChartConfig, ChartContainer } from "design-system/molecules/Chart"
import { useMemo } from "react"
import type { MouseEvent } from "react"
import { Treemap, TreemapNode } from "recharts"
import { BackofficeFlagsKindEnum, GetBackofficeFlagsTreemapResponseDataSchema } from "schema"
import { z } from "zod"

const chartConfig = {} satisfies ChartConfig
const NAMESPACE_HEADER_HEIGHT = 24
const NAMESPACE_CONTENT_INSET = 8
const CELL_PADDING = 2
const SECTION_LABEL_PADDING_X = 12
const SECTION_LABEL_PADDING_Y = 24

type TreemapData = Extract<
  z.infer<typeof GetBackofficeFlagsTreemapResponseDataSchema>,
  { groupBy: "key" }
>["namespaces"]
type FlagKind = (typeof BackofficeFlagsKindEnum)[number]

const KIND_PALETTE = {
  Issue: "var(--testing-2)",
  Friction: "var(--neutral)",
} satisfies Record<FlagKind, string>

type NamespaceNode = {
  nodeType: "namespace"
  name: string
  namespace: string
  namespaceLabel: string
  count: number
  children: KeyNode[]
}

type KeyNode = {
  nodeType: "key"
  name: string
  namespace: string
  namespaceLabel: string
  // Not named `key`: React reserves it and strips it from cloneElement props (Recharts).
  flagKey: string
  kind: FlagKind
  count: number
  fill: string
}

export type FlagsTreemapTooltipData = {
  namespace: string
  namespaceLabel: string
  key: string
  kind: FlagKind
  count: number
  coordinate: {
    x: number
    y: number
  }
}

type Props = {
  data: TreemapData
  getNamespaceLabel: (namespace: string) => string
  onNamespaceClick: (namespace: string) => void
  onKeyClick: (key: string) => void
  onTooltipChange?: (tooltip: FlagsTreemapTooltipData | null) => void
}

const getBareFlagKey = (fullKey: string) => {
  const [namespace = fullKey, ...keyParts] = fullKey.split(":")
  return keyParts.join(":") || namespace
}

const truncateLabel = (label: string, width: number) => {
  const maxCharacters = Math.max(Math.floor((width - 16) / 7), 0)
  if (label.length <= maxCharacters) return label
  if (maxCharacters <= 1) return ""
  return `${label.slice(0, maxCharacters - 1)}…`
}

export function FlagsTreemapChart({
  data,
  getNamespaceLabel,
  onNamespaceClick,
  onKeyClick,
  onTooltipChange,
}: Props) {
  const chartData = useMemo(() => {
    return data.map((namespace): NamespaceNode => ({
      nodeType: "namespace",
      name: getNamespaceLabel(namespace.namespace),
      namespace: namespace.namespace,
      namespaceLabel: getNamespaceLabel(namespace.namespace),
      count: namespace.count,
      children: namespace.keys.map((key) => ({
        nodeType: "key",
        name: key.key,
        namespace: namespace.namespace,
        namespaceLabel: getNamespaceLabel(namespace.namespace),
        flagKey: key.key,
        kind: key.kind,
        count: key.count,
        fill: KIND_PALETTE[key.kind],
      })),
    }))
  }, [data, getNamespaceLabel])

  return (
    <ChartContainer config={chartConfig} className="h-[280px] w-full">
      <Treemap
        data={chartData}
        dataKey="count"
        nameKey="name"
        content={
          <FlagsTreemapContent onNamespaceClick={onNamespaceClick} onKeyClick={onKeyClick} />
        }
        onMouseEnter={(node, event) => {
          const leaf = node as TreemapNode & Partial<KeyNode>
          if (leaf.nodeType !== "key" || !leaf.flagKey || !leaf.kind) return

          const mouseEvent = event as MouseEvent<SVGElement>
          onTooltipChange?.({
            namespace: leaf.namespace ?? "",
            namespaceLabel: leaf.namespaceLabel ?? "",
            key: leaf.flagKey,
            kind: leaf.kind,
            count: leaf.count ?? 0,
            coordinate: {
              x: mouseEvent.clientX + 12,
              y: mouseEvent.clientY + 12,
            },
          })
        }}
        onMouseLeave={() => onTooltipChange?.(null)}
        isAnimationActive={false}
      />
    </ChartContainer>
  )
}

type ContentProps = Partial<TreemapNode> & {
  onNamespaceClick: (namespace: string) => void
  onKeyClick: (key: string) => void
}

function FlagsTreemapContent(props: ContentProps) {
  const { x = 0, y = 0, width = 0, height = 0, depth = 0, onNamespaceClick, onKeyClick } = props
  if (depth === 0) return null

  const node = props as unknown as (NamespaceNode | KeyNode) & {
    root?: TreemapNode
  }

  if (node.nodeType === "namespace") {
    const label = truncateLabel(node.namespaceLabel, width)

    return (
      <g
        role="button"
        tabIndex={0}
        aria-label={node.namespaceLabel}
        className="cursor-pointer"
        onClick={() => onNamespaceClick(node.namespace)}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return
          event.preventDefault()
          onNamespaceClick(node.namespace)
        }}
      >
        <rect
          x={x + 1}
          y={y + 1}
          width={Math.max(width - 2, 0)}
          height={Math.max(height - 2, 0)}
          fill="transparent"
          stroke="var(--border)"
          strokeDasharray="2 2"
          strokeWidth={1}
          rx={4}
        />
        {label && height >= NAMESPACE_HEADER_HEIGHT && (
          <text
            x={x + 10}
            y={y + 20}
            fill="var(--muted-foreground)"
            fontSize={13}
            fontWeight={600}
            pointerEvents="none"
          >
            {label}
          </text>
        )}
      </g>
    )
  }

  if (node.nodeType !== "key") return null

  const parent = node.root
  const parentX = parent?.x ?? x
  const parentY = parent?.y ?? y
  const parentWidth = parent?.width ?? width
  const parentHeight = parent?.height ?? height
  const contentX = parentX + NAMESPACE_CONTENT_INSET
  const contentY = parentY + NAMESPACE_HEADER_HEIGHT + NAMESPACE_CONTENT_INSET
  const contentWidth = Math.max(parentWidth - NAMESPACE_CONTENT_INSET * 2, 0)
  const contentHeight = Math.max(
    parentHeight - NAMESPACE_HEADER_HEIGHT - NAMESPACE_CONTENT_INSET * 2,
    0,
  )
  const widthRatio = parentWidth > 0 ? contentWidth / parentWidth : 0
  const heightRatio = parentHeight > 0 ? contentHeight / parentHeight : 0
  const adjustedX = contentX + (x - parentX) * widthRatio
  const adjustedY = contentY + (y - parentY) * heightRatio
  const adjustedWidth = width * widthRatio
  const adjustedHeight = height * heightRatio
  const innerX = adjustedX + CELL_PADDING
  const innerY = adjustedY + CELL_PADDING
  const innerWidth = Math.max(adjustedWidth - CELL_PADDING * 2, 0)
  const innerHeight = Math.max(adjustedHeight - CELL_PADDING * 2, 0)
  const labelWidth = Math.max(innerWidth - SECTION_LABEL_PADDING_X * 2, 0)
  const label = truncateLabel(getBareFlagKey(node.flagKey), labelWidth)

  return (
    <g
      role="button"
      tabIndex={0}
      aria-label={node.flagKey}
      className="group cursor-pointer"
      onClick={() => onKeyClick(node.flagKey)}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return
        event.preventDefault()
        onKeyClick(node.flagKey)
      }}
    >
      <rect
        x={innerX}
        y={innerY}
        width={innerWidth}
        height={innerHeight}
        fill={node.fill}
        stroke="var(--background)"
        strokeWidth={1}
        rx={3}
        className="opacity-60 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
      />
      {label && innerHeight >= SECTION_LABEL_PADDING_Y + 12 && (
        <text
          x={innerX + SECTION_LABEL_PADDING_X}
          y={innerY + SECTION_LABEL_PADDING_Y}
          fill="var(--foreground)"
          fontSize={12}
          fontWeight={500}
          pointerEvents="none"
        >
          {label}
        </text>
      )}
    </g>
  )
}
